"use client";

import { Button } from "@/components/ui/button";
import type { AppLocale } from "@/i18n/config";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { DownloadAppPromo } from "./DownloadAppPromo";

interface SuccessStepProps {
  /** Active locale — selects the localized app-promo banner. */
  locale: string;
  /** Closes the signup sheet (the funnel is done). */
  onFinish: () => void;
}

/**
 * Terminal success step (design `14513:5599`): a trophy illustration, the
 * confirmation copy, the localized "Get the Spond app" banner ({@link
 * DownloadAppPromo} — the same asset used elsewhere), and a brand (pink) Finish
 * button that closes the sheet. The trophy is decorative (alt="") — the heading
 * carries the meaning.
 */
export default function SuccessStep({ locale, onFinish }: SuccessStepProps) {
  const t = useTranslations("publicEventSignup");

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-1 flex-col items-center justify-center gap-6 pt-8 text-center">
        <Image
          src="/images/signup-success-trophy.png"
          alt="Trophy"
          width={224}
          height={224}
          className="h-56 w-56 object-contain"
        />
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold">{t("success.header")}</h2>
          <p className="text-base text-foreground">{t("success.body")}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <DownloadAppPromo locale={locale as AppLocale} />
        <Button
          type="button"
          variant="brand"
          onClick={onFinish}
          className="w-full rounded-full"
        >
          {t("success.finish")}
        </Button>
      </div>
    </div>
  );
}
